import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Typography, Button, Card, CardContent, Chip, Alert, CircularProgress } from '@mui/material';
import { Description as ReportIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../../PageLayout';
import {
  getAdviseEngagementSubject,
  getAdviseEngagementLoadingSubject,
  useAdviseEngagements
} from '../../../store/AdviseEngagement';
import { getAdviseSupplierSubject, useAdviseSuppliers } from '../../../store/AdviseSupplier';
import { getAdviseCustomerSubject, useAdviseCustomers } from '../../../store/AdviseCustomer';
import { AdviseEngagement, AdviseSupplier, AdviseCustomer } from '../../../types/advise';
import { getSelectedAccountsSubject, hasAdviseFull } from '../../../store/User';
import { ReportGenerator } from '../../Compounds/Advise/Reports';
import { AdviseSubNav } from '../../Compounds/Advise/AdviseSubNav';
import { AdviseAccountGuard } from '../../Compounds/Advise/AdviseAccountGuard';

export const AdviseReportsPage = () => {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState<AdviseEngagement[]>([]);
  const [suppliers, setSuppliers] = useState<AdviseSupplier[]>([]);
  const [customers, setCustomers] = useState<AdviseCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [generateFor, setGenerateFor] = useState<AdviseEngagement | null>(null);

  const { fetchEngagements } = useAdviseEngagements();
  const { fetchSuppliers } = useAdviseSuppliers();
  const { fetchCustomers } = useAdviseCustomers();
  const canEdit = hasAdviseFull();

  useEffect(() => {
    const subs = [
      getAdviseEngagementSubject().subscribe(setEngagements),
      getAdviseEngagementLoadingSubject().subscribe(setLoading),
      getAdviseSupplierSubject().subscribe(setSuppliers),
      getAdviseCustomerSubject().subscribe(setCustomers)
    ];
    return () => subs.forEach((s) => s.unsubscribe());
  }, []);

  useEffect(() => {
    fetchEngagements();
    fetchSuppliers();
    fetchCustomers();

    const accountSub = getSelectedAccountsSubject().subscribe(() => {
      fetchEngagements(true);
      fetchCustomers(true);
    });
    return () => accountSub.unsubscribe();
  }, []);

  const getCustomerName = useCallback(
    (customerId: string) => {
      return customers.find((customer) => customer.id === customerId)?.company_name || '—';
    },
    [customers]
  );

  const getSupplierCount = useCallback(
    (engagement: AdviseEngagement) => engagement.selected_suppliers?.length || 0,
    []
  );

  const activeEngagements = useMemo(() => engagements.filter((c) => c.status !== 'archived'), [engagements]);

  if (loading && engagements.length === 0) {
    return (
      <PageLayout
        pageTitle="Advise Reports"
        dataCy="advise-reports-page">
        <Box className="flex justify-center p-12">
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle="Advise Reports"
      dataCy="advise-reports-page">
      <AdviseSubNav />
      <AdviseAccountGuard>
        <Box className="flex flex-col gap-4">
          <Typography
            variant="body2"
            color="textSecondary">
            Generate professional reports from your engagements. Select an engagement below to create a PDF-ready
            report.
          </Typography>

          {activeEngagements.length === 0 ? (
            <Alert severity="info">No engagements available. Create an engagement first to generate reports.</Alert>
          ) : (
            <Box className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {activeEngagements.map((engagement) => (
                <Card
                  key={engagement.id}
                  variant="outlined">
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      gutterBottom>
                      {engagement.engagement_name}
                    </Typography>
                    <Box className="flex items-center gap-2 mb-2">
                      <Chip
                        label={engagement.product_type || 'CX'}
                        size="small"
                        variant="outlined"
                      />
                      <Typography
                        variant="caption"
                        color="textSecondary">
                        {getSupplierCount(engagement)} suppliers
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom>
                      Customer: {getCustomerName(engagement.customer_id)}
                    </Typography>
                    <Box className="flex gap-2 mt-2">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ReportIcon />}
                        onClick={() => setGenerateFor(engagement)}>
                        Generate
                      </Button>
                      {canEdit && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => navigate(`/advise/report-editor?id=${engagement.id}`)}>
                          Layout
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        {generateFor && (
          <ReportGenerator
            engagement={generateFor}
            suppliers={suppliers}
            customer={customers.find((c) => c.id === generateFor.customer_id) || null}
            open={!!generateFor}
            onClose={() => setGenerateFor(null)}
          />
        )}
      </AdviseAccountGuard>
    </PageLayout>
  );
};
